import type { ParsedStatement, ParsedTransaction } from "@paisa/types"
import * as XLSX from "xlsx"
import { parseAmount } from "../lib/parse-helpers"
import { parseHdfcNarration } from "./narration"

/** Column names in HDFC savings/current account statements */
const COLUMNS = {
  date: "Date",
  narration: "Narration",
  refNo: "Chq./Ref.No.",
  valueDate: "Value Dt",
  withdrawal: "Withdrawal Amt.",
  deposit: "Deposit Amt.",
  closingBalance: "Closing Balance",
} as const

/**
 * Detect whether an XLS/CSV file is an HDFC savings/current account statement.
 * Checks for HDFC-specific header patterns and column names.
 */
export function isHdfcSavings(rawContent: string, headers?: string[]): boolean {
  const hasHdfcHeader = rawContent.includes("HDFC BANK Ltd")
  const hasColumns =
    !!headers && headers.includes(COLUMNS.narration) && headers.includes(COLUMNS.refNo)
  return hasHdfcHeader || hasColumns
}

/**
 * Parse an HDFC savings/current account XLS statement.
 * Handles the metadata header rows, finds the data table, and extracts transactions.
 */
export function parseHdfcSavings(data: ArrayBuffer): ParsedStatement {
  const workbook = XLSX.read(new Uint8Array(data), { type: "array" })
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet) throw new Error("No sheet found in workbook")

  // Convert to array of arrays — raw rows including headers
  const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })

  // Extract metadata from header rows
  const metadata = extractMetadata(rows)

  // Find the header row (contains "Date", "Narration", etc.)
  const headerIdx = findHeaderRow(rows)
  if (headerIdx === -1) throw new Error("Could not find column headers in HDFC statement")

  const headerRow = rows[headerIdx] as string[]
  const colMap = mapColumns(headerRow)

  // Parse transaction rows (skip header + separator row)
  const transactions: ParsedTransaction[] = []
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i] as unknown[]
    if (!row || row.length === 0) continue

    // Skip separator rows (all asterisks)
    const firstCell = String(row[0] ?? "").trim()
    if (firstCell.startsWith("***") || firstCell === "") continue

    const txn = parseRow(row, colMap)
    if (txn) transactions.push(txn)
  }

  return {
    bank: "hdfc",
    accountType: metadata.accountType,
    accountNumber: metadata.accountLast4,
    periodStart: metadata.periodStart,
    periodEnd: metadata.periodEnd,
    transactions,
  }
}

// ── Metadata extraction ──────────────────────────────────────────────────

interface StatementMetadata {
  accountType: "savings" | "current"
  accountLast4: string | null
  periodStart: string | null
  periodEnd: string | null
}

function extractMetadata(rows: unknown[][]): StatementMetadata {
  let accountType: "savings" | "current" = "savings"
  let accountLast4: string | null = null
  let periodStart: string | null = null
  let periodEnd: string | null = null

  // Scan first 25 rows for metadata
  for (let i = 0; i < Math.min(25, rows.length); i++) {
    const rowText = (rows[i] ?? []).map((c) => String(c ?? "")).join(" ")

    // Account number: "Account No :50100565912523   PRIME POTENTIAL"
    const accountMatch = rowText.match(/Account No\s*:\s*(\d+)/)
    if (accountMatch) {
      const fullNum = accountMatch[1]
      accountLast4 = fullNum.slice(-4)
    }

    // Account type detection from account description
    if (/current/i.test(rowText) && /account/i.test(rowText)) {
      accountType = "current"
    }

    // Statement period: "Statement From  :  01/04/2025         To  :  31/03/2026"
    const periodMatch = rowText.match(
      /Statement From\s*:\s*(\d{2}\/\d{2}\/\d{4})\s+To\s*:\s*(\d{2}\/\d{2}\/\d{4})/,
    )
    if (periodMatch) {
      periodStart = parseDate(periodMatch[1])
      periodEnd = parseDate(periodMatch[2])
    }
  }

  return { accountType, accountLast4, periodStart, periodEnd }
}

// ── Column detection ─────────────────────────────────────────────────────

interface ColumnMap {
  date: number
  narration: number
  refNo: number
  valueDate: number
  withdrawal: number
  deposit: number
  closingBalance: number
}

function findHeaderRow(rows: unknown[][]): number {
  for (let i = 0; i < Math.min(30, rows.length); i++) {
    const row = (rows[i] ?? []).map((c) => String(c ?? "").trim())
    if (row.includes(COLUMNS.date) && row.includes(COLUMNS.narration)) {
      return i
    }
  }
  return -1
}

function mapColumns(headerRow: string[]): ColumnMap {
  const find = (name: string): number => {
    const idx = headerRow.findIndex((h) => h.trim() === name)
    if (idx === -1) throw new Error(`Column "${name}" not found in HDFC statement`)
    return idx
  }

  return {
    date: find(COLUMNS.date),
    narration: find(COLUMNS.narration),
    refNo: find(COLUMNS.refNo),
    valueDate: find(COLUMNS.valueDate),
    withdrawal: find(COLUMNS.withdrawal),
    deposit: find(COLUMNS.deposit),
    closingBalance: find(COLUMNS.closingBalance),
  }
}

// ── Row parsing ──────────────────────────────────────────────────────────

function parseRow(row: unknown[], colMap: ColumnMap): ParsedTransaction | null {
  const date = parseDateCell(row[colMap.date])
  if (!date) return null

  const narration = String(row[colMap.narration] ?? "").trim()
  if (!narration) return null

  const withdrawal = parseAmount(row[colMap.withdrawal])
  const deposit = parseAmount(row[colMap.deposit])

  // Skip rows with no amount
  if (withdrawal === 0 && deposit === 0) return null

  const amount = withdrawal > 0 ? withdrawal : deposit
  const type = withdrawal > 0 ? "debit" : "credit"

  const refNo = String(row[colMap.refNo] ?? "").trim() || null
  const valueDate = parseDateCell(row[colMap.valueDate])
  const closingBalance = parseAmount(row[colMap.closingBalance]) || null

  // Extract structured info from narration
  const parsed = parseHdfcNarration(narration)

  return {
    date,
    description: narration,
    amount,
    type: type as "debit" | "credit",
    referenceNumber: refNo,
    valueDate,
    closingBalance,
    paymentMethod: parsed.paymentMethod,
    extractedName: parsed.name,
    upiId: parsed.upiId,
    sourceCategory: null,
  }
}

// ── Utilities ────────────────────────────────────────────────────────────

/**
 * Parse a date cell from XLSX. Handles three forms:
 *   - JS Date object (when cellDates:true or XLSX auto-converts)
 *   - Excel serial number (e.g. 46164)
 *   - "DD/MM/YY" or "DD/MM/YYYY" string
 */
function parseDateCell(cell: unknown): string | null {
  if (cell instanceof Date) {
    return dateToIso(cell)
  }
  if (typeof cell === "number" && cell > 0) {
    // Excel serial → JS Date (UTC). Subtract 25569 (days from Excel epoch 1900-01-00
    // to Unix epoch 1970-01-01, accounting for Excel's phantom Feb 29 1900 bug).
    const ms = (cell - 25569) * 86400 * 1000
    return dateToIso(new Date(ms))
  }
  const raw = String(cell ?? "").trim()
  if (!raw) return null
  // DD/MM/YY or DD/MM/YYYY
  const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{2,4})$/)
  if (!match) return null
  const day = match[1]
  const month = match[2]
  let year = match[3]
  if (year.length === 2) {
    const num = parseInt(year, 10)
    year = num > 50 ? `19${year}` : `20${year}`
  }
  return `${year}-${month}-${day}`
}

function dateToIso(d: Date): string {
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, "0")
  const day = String(d.getUTCDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function parseDate(raw: string): string | null {
  return parseDateCell(raw)
}
